import { Search, Settings, Bookmark } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';

interface OptionsMenuProps {
  searchEnabled: boolean;
  aiEnabled: boolean;
  rememberState: boolean;
  onSearchToggle: (enabled: boolean) => void;
  onAIToggle: (enabled: boolean) => void;
  onRememberToggle: (enabled: boolean) => void;
  disabled?: boolean;
  /**
   * When true, the user has 0 spendable (subscription + fias) credits. The
   * Fias AI toggle is rendered as disabled and clicking it sends them to the
   * credits store instead of toggling.
   */
  aiOutOfCredits?: boolean;
}

export function OptionsMenu({
  searchEnabled,
  aiEnabled,
  rememberState,
  onSearchToggle,
  onAIToggle,
  onRememberToggle,
  disabled = false,
  aiOutOfCredits = false,
}: OptionsMenuProps) {
  const navigate = useNavigate();

  // Preserve the invariant that at least one of {search, AI} stays on.
  const canDisableSearch = aiEnabled;
  const canDisableAI = searchEnabled;

  const handleSearchChange = (next: boolean) => {
    if (!next && !canDisableSearch) return;
    onSearchToggle(next);
  };

  const handleAIChange = (next: boolean) => {
    if (aiOutOfCredits) {
      navigate('/credits');
      return;
    }
    if (!next && !canDisableAI) return;
    onAIToggle(next);
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          size="icon"
          disabled={disabled}
          title="Options"
          aria-label="Options"
        >
          <Settings className="h-4 w-4" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="center" className="w-72">
        <div className="space-y-4">
          <div>
            <h4 className="text-sm font-semibold leading-none">Options</h4>
            <p className="mt-1 text-xs text-muted-foreground">Choose what runs when you submit.</p>
          </div>

          <div className="space-y-3">
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-start gap-2 min-w-0">
                <Search className="h-4 w-4 mt-0.5 text-muted-foreground shrink-0" />
                <div className="min-w-0">
                  <Label htmlFor="opt-arche-search" className="text-sm font-medium">
                    Arche Search
                  </Label>
                  <p className="text-xs text-muted-foreground">
                    Find Arches related to your message.
                  </p>
                </div>
              </div>
              <Switch
                id="opt-arche-search"
                checked={searchEnabled}
                onCheckedChange={handleSearchChange}
                disabled={disabled || (searchEnabled && !canDisableSearch)}
              />
            </div>

            <div className="flex items-start justify-between gap-3">
              <div className="flex items-start gap-2 min-w-0">
                <img
                  src="/Heptagen_Logo_1.png"
                  alt=""
                  className="h-4 w-4 mt-0.5 object-contain shrink-0"
                />
                <div className="min-w-0">
                  <Label htmlFor="opt-fias-ai" className="text-sm font-medium">
                    Fias AI
                  </Label>
                  <p className="text-xs text-muted-foreground">
                    {aiOutOfCredits
                      ? 'Out of credits — visit the store to top up.'
                      : 'Answer with Fias AI.'}
                  </p>
                </div>
              </div>
              <Switch
                id="opt-fias-ai"
                checked={aiEnabled && !aiOutOfCredits}
                onCheckedChange={handleAIChange}
                disabled={disabled || (aiEnabled && !canDisableAI)}
              />
            </div>
          </div>

          <div className="border-t pt-3">
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-start gap-2 min-w-0">
                <Bookmark className="h-4 w-4 mt-0.5 text-muted-foreground shrink-0" />
                <div className="min-w-0">
                  <Label htmlFor="opt-remember" className="text-sm font-medium">
                    Remember this state
                  </Label>
                  <p className="text-xs text-muted-foreground">
                    Keep these settings across visits. When off, they reset after 30 minutes of
                    inactivity.
                  </p>
                </div>
              </div>
              <Switch
                id="opt-remember"
                checked={rememberState}
                onCheckedChange={onRememberToggle}
                disabled={disabled}
              />
            </div>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
